import { Injectable } from '@angular/core';

import { BaseStorage } from '../../core/abstractions/base.storage';
import { StorageTypeEnum } from '../../models/enums/storage-type.enum';
import { UserModel } from '../../models/user.model';

/**
 * Stores the authenticated user info, including the session JWT.
 *
 * Security (Issue #5): the token must NOT live in `localStorage` — that is
 * XSS-readable, persists across browser restarts and leaks across tabs. Instead
 * it is kept in `sessionStorage` plus an in-memory cache, which limits
 * persistence and cross-tab exposure. The preferred hardening (in-memory access
 * token + httpOnly refresh cookie) requires backend support and is tracked
 * separately.
 *
 * All token reads across the app go through this class — never read
 * `localStorage`/`sessionStorage` directly.
 */
@Injectable({
    providedIn: 'root'
})
export class SecurityStorage extends BaseStorage {
    private userInfoKey = 'userInfo';

    /**
     * In-memory copy of the user info. Serves navigation within the tab without
     * re-parsing storage and is wiped on logout.
     */
    private cachedUserInfo?: UserModel;

    constructor() {
        super('security', StorageTypeEnum.Session);
        this.purgeLegacyLocalStorage();
    }

    saveUserInfo(userInfo: UserModel): void {
        this.cachedUserInfo = userInfo;
        this.save(userInfo, this.userInfoKey);
    }

    getUserInfo(): UserModel {
        if (this.cachedUserInfo) {
            return this.cachedUserInfo;
        }

        // Fall back to sessionStorage (e.g. after a page reload in the same tab)
        // and re-populate the in-memory cache.
        const stored = this.get<UserModel>(this.userInfoKey);

        if (stored) {
            this.cachedUserInfo = stored;
        }

        return stored;
    }

    eraseUserInfo(): void {
        this.cachedUserInfo = undefined;
        this.remove(this.userInfoKey);
    }

    /**
     * One-time purge of the pre-Issue-#5 token that used to live in
     * `localStorage`. Guarantees the acceptance criterion "token is not in
     * localStorage" even for users upgrading with an existing session — they are
     * asked to sign in once more. Guarded for Safari private mode where
     * `localStorage` access can throw.
     */
    private purgeLegacyLocalStorage(): void {
        try {
            window.localStorage.removeItem('thumedi.security.userInfo');
        } catch {
            // Ignore: localStorage may be unavailable (e.g. Safari private mode).
        }
    }
}
